/**
 * LBank REST API Client
 * @see https://www.lbank.com/docs/
 */

import type {
  LbankAccuracyResponse,
  LbankDepthResponse,
  LbankInternalKlinesResponse,
  LbankKlinesResponse,
  LbankTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.lbkex.com';

/**
 * LBank internal API base URL (used by their web frontend).
 * This API supports 8h/12h intervals that the public API doesn't.
 */
const INTERNAL_BASE_URL = 'https://ccapi.rerrkvifj.com';

/**
 * LBank REST API endpoints.
 */
export const lbankRestEndpoints = {
  /** 24-hour ticker statistics */
  ticker24h: `${BASE_URL}/v2/ticker/24hr.do`,
  /** Best bid/ask prices */
  bookTicker: `${BASE_URL}/v2/supplement/ticker/bookTicker.do`,
  /** Latest price */
  price: `${BASE_URL}/v2/supplement/ticker/price.do`,
  /** Orderbook depth */
  depth: `${BASE_URL}/v2/depth.do`,
  /** Recent trades */
  trades: `${BASE_URL}/v2/supplement/trades.do`,
  /** K-line (candlestick) data - public API (no 8h/12h support) */
  klines: `${BASE_URL}/v2/kline.do`,
  /** K-line (candlestick) data - internal API (supports all intervals including 8h/12h) */
  klinesInternal: `${INTERNAL_BASE_URL}/spot-market-center/klines`,
  /** Trading pair accuracy (precision) info */
  accuracy: `${BASE_URL}/v2/accuracy.do`,
} as const;

// ========================================
// Helper Functions
// ========================================

/**
 * Format symbol for LBank API calls.
 * LBank uses lowercase underscore format: "btc_usdt", "eth_usdt".
 *
 * @example
 * formatLbankSymbol("BTC", "USDT") // "btc_usdt"
 */
export function formatLbankSymbol(base: string, quote: string): string {
  return `${base}_${quote}`.toLowerCase();
}

/**
 * Parse LBank symbol into base/quote.
 *
 * @example
 * parseLbankSymbol("btc_usdt") // { base: "BTC", quote: "USDT" }
 */
export function parseLbankSymbol(symbol: string): { base: string; quote: string } | null {
  const parts = symbol.split('_');
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return null;
  }
  return {
    base: parts[0].toUpperCase(),
    quote: parts[1].toUpperCase(),
  };
}

// ========================================
// REST API Functions
// ========================================

export interface LbankTicker24hRequest {
  /** Trading pair symbol (e.g., btc_usdt) */
  symbol: string;
}

export interface LbankTicker24hResponse {
  result: boolean;
  error_code: number;
  reason: string | null;
  data: {
    symbol: string;
    ticker: {
      /** Latest traded price */
      latest: string;
      /** 24h high price */
      high: string;
      /** 24h low price */
      low: string;
      /** 24h price change percentage */
      change: string;
      /** 24h base volume */
      vol: string;
      /** 24h quote volume (turnover) */
      turnover: string;
      [key: string]: unknown;
    };
    timestamp: number;
  }[];
}

/**
 * Fetches 24-hour ticker statistics for a symbol.
 */
export const getLbankTicker24h = async (
  params: LbankTicker24hRequest,
  options?: RestRequestOptions,
): Promise<LbankTicker24hResponse> => {
  const response = await get<LbankTicker24hResponse, LbankTicker24hRequest>({
    url: lbankRestEndpoints.ticker24h,
    params,
    options,
  });

  return response.data;
};

export interface LbankBookTickerRequest {
  /** Trading pair symbol (e.g., "btc_usdt") */
  symbol: string;
}

export interface LbankBookTickerResponse {
  result: boolean;
  error_code: number;
  reason: string | null;
  data: {
    /** Trading pair symbol */
    symbol: string;
    /** Best bid price */
    bidPrice: string;
    /** Best bid quantity (if provided) */
    bidQty?: string;
    /** Best ask price */
    askPrice: string;
    /** Best ask quantity (if provided) */
    askQty?: string;
    /** Server timestamp (optional) */
    time?: number;
  };
}

/**
 * Fetches best bid/ask prices (book ticker) for a symbol.
 */
export const getLbankBookTicker = async (
  params: LbankBookTickerRequest,
  options?: RestRequestOptions,
): Promise<LbankBookTickerResponse> => {
  const response = await get<LbankBookTickerResponse, LbankBookTickerRequest>({
    url: lbankRestEndpoints.bookTicker,
    params,
    options,
  });

  return response.data;
};

export interface LbankDepthRequest {
  /** Trading pair symbol (e.g., "btc_usdt") */
  symbol: string;
  /** Depth limit: 1-200 */
  size: number;
}

/**
 * Fetches orderbook depth (snapshot) for a symbol.
 */
export const getLbankDepth = async (
  params: LbankDepthRequest,
  options?: RestRequestOptions,
): Promise<LbankDepthResponse> => {
  const response = await get<LbankDepthResponse>({
    url: lbankRestEndpoints.depth,
    params: {
      symbol: params.symbol,
      size: params.size,
    },
    options,
  });

  return response.data;
};

export interface LbankTradesRequest {
  /** Trading pair symbol (e.g., "btc_usdt") */
  symbol: string;
  /** Number of trades to return */
  size: number;
  /** Optional: Return trades after this timestamp */
  time?: number;
}

/**
 * Fetches recent public trades for a symbol.
 */
export const getLbankTrades = async (
  params: LbankTradesRequest,
  options?: RestRequestOptions,
): Promise<LbankTradesResponse> => {
  const queryParams: Record<string, string | number> = {
    symbol: params.symbol,
    size: params.size,
  };

  if (params.time !== undefined) {
    queryParams.time = params.time;
  }

  const response = await get<LbankTradesResponse>({
    url: lbankRestEndpoints.trades,
    params: queryParams,
    options,
  });

  return response.data;
};

/**
 * LBank supported kline intervals (public API format).
 */
export type LbankKlineInterval =
  | 'minute1'
  | 'minute5'
  | 'minute15'
  | 'minute30'
  | 'hour1'
  | 'hour4'
  | 'hour8'
  | 'hour12'
  | 'day1'
  | 'week1'
  | 'month1';

/**
 * LBank internal API kline intervals (short format).
 */
export type LbankInternalKlineInterval =
  | '1m'
  | '5m'
  | '15m'
  | '30m'
  | '1h'
  | '4h'
  | '8h'
  | '12h'
  | '1d'
  | '1w'
  | '1M';

/**
 * Maps public API interval format to internal API format.
 */
export const mapToLbankInternalInterval = (
  interval: LbankKlineInterval,
): LbankInternalKlineInterval => {
  const mapping: Record<LbankKlineInterval, LbankInternalKlineInterval> = {
    minute1: '1m',
    minute5: '5m',
    minute15: '15m',
    minute30: '30m',
    hour1: '1h',
    hour4: '4h',
    hour8: '8h',
    hour12: '12h',
    day1: '1d',
    week1: '1w',
    month1: '1M',
  };
  return mapping[interval];
};

export interface LbankKlinesRequest {
  /** Trading pair symbol (e.g., "btc_usdt") */
  symbol: string;
  /** Kline interval */
  type: LbankKlineInterval;
  /** Number of candles to return: 1-2000 */
  size: number;
  /** Start time (Unix timestamp in seconds) */
  time: number;
}

/**
 * Fetches historical klines (candlesticks) from LBank.
 */
export const getLbankKlines = async (
  params: LbankKlinesRequest,
  options?: RestRequestOptions,
): Promise<LbankKlinesResponse> => {
  const response = await get<LbankKlinesResponse>({
    url: lbankRestEndpoints.klines,
    params: {
      symbol: params.symbol,
      type: params.type,
      size: params.size,
      time: params.time,
    },
    options,
  });

  return response.data;
};

export interface LbankKlinesInternalRequest {
  /** Trading pair symbol (e.g., "btc_usdt") */
  symbol: string;
  /** Kline interval in short format (e.g., "1m", "8h", "12h") */
  interval: LbankInternalKlineInterval;
  /** Number of candles to return: 1-2000 */
  size: number;
  /** End time (Unix timestamp in milliseconds) */
  to: number;
}

/**
 * Fetches historical klines from LBank's internal API.
 * This API supports all intervals including 8h and 12h.
 */
export const getLbankKlinesInternal = async (
  params: LbankKlinesInternalRequest,
  options?: RestRequestOptions,
): Promise<LbankInternalKlinesResponse> => {
  const response = await get<LbankInternalKlinesResponse>({
    url: lbankRestEndpoints.klinesInternal,
    params: {
      symbol: params.symbol,
      interval: params.interval,
      size: params.size,
      to: params.to,
    },
    options,
  });

  return response.data;
};

export interface LbankAccuracyRequest {
  /** Optional: Trading pair symbol to filter (e.g., "btc_usdt") */
  symbol?: string;
}

/**
 * Fetches trading pair accuracy (precision) information.
 */
export const getLbankAccuracy = async (
  params?: LbankAccuracyRequest,
  options?: RestRequestOptions,
): Promise<LbankAccuracyResponse> => {
  const queryParams: Record<string, string> = {};

  if (params?.symbol) {
    queryParams.symbol = params.symbol;
  }

  const response = await get<LbankAccuracyResponse>({
    url: lbankRestEndpoints.accuracy,
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    options,
  });

  return response.data;
};
